import type { SignedReviewFormValue, UnsignedReviewFormValue } from "./types"
import { BRAND_REASON, NON_BRAND_REASONS } from "./constants"

/**
 * 复盘表单校验（spec 第 5 节，与后端 service/project 的提交校验口径一致）
 *
 * 抽取为纯函数以便 BDD 测试（对应 project_gross_profit_review_web.feature 的校验场景）：
 * 返回错误文案（由调用方 message.warning 提示且不发起请求），通过时返回 null。
 */

/** A1 成员工时列表状态（校验 A1 勾选必填规则用） */
export interface WorktimesState {
    /** 是否已成功加载过 */
    loaded: boolean
    /** 是否加载失败 */
    error: boolean
    /** 已加载的工时记录条数 */
    count: number
}

/** 已签约项目毛利复盘校验（spec 5.1，校验顺序与表格一致）
 * 亏损原因判定可多选（A / B 可同时勾选），至少选择一项；选中哪项就校验哪项的必填内容 */
export const validateSignedForm = (
    value: SignedReviewFormValue,
    worktimes: WorktimesState
): string | null => {
    const reasonTypes = value.reasonTypes ?? []
    if (reasonTypes.length === 0)
        return "请至少选择一项亏损原因判定（A 数据有问题 / B 项目操作原因）"

    if (reasonTypes.includes("A")) {
        if (!value.a1Checked && !value.a2Checked && !value.a3Checked)
            return "请至少勾选一项数据问题（A1 / A2 / A3）"
        // 工时列表为空或加载失败时不强制勾选（避免接口异常导致无法填报）
        if (
            value.a1Checked &&
            worktimes.loaded &&
            !worktimes.error &&
            worktimes.count > 0 &&
            value.a1Worktimes.length === 0
        )
            return "A1 工时填报错误：请勾选填错的人员（工时列表）"
        // 修正后的工日数仅允许大于等于 0 的数字
        if (
            value.a1Checked &&
            value.a1Worktimes.some(
                (w) => w.correctedTotalHours !== undefined && (isNaN(w.correctedTotalHours) || w.correctedTotalHours < 0)
            )
        )
            return "修正后的工日数仅支持大于等于 0 的数字"
        // A2 里程碑填报错误勾选后仅展示跳转链接，无填写项，不参与校验
        if (value.a3Checked && !value.a3Desc.trim())
            return "A3：请填写具体描述"
    }

    if (reasonTypes.includes("B")) {
        if (!value.bCategory) return "请选择“品牌项目”或“非品牌项目”（二选一）"
        if (value.bCategory === "brand") {
            if (!value.b0Desc.trim()) return `B0 ${BRAND_REASON.label}：请填写具体描述`
        } else {
            const checked = NON_BRAND_REASONS.filter((r) => value.bReasons[r.key])
            if (checked.length === 0) return "请至少勾选一项非品牌项目原因类别"
            // 提示文案与界面展示的类别名一致（如「B1 甲方原因」）
            const empty = checked.find((r) => !(value.bReasonDesc[r.key] || "").trim())
            if (empty) return `${empty.key} ${empty.label}：请填写具体描述`
        }
    }

    if (!value.improvement.trim()) return "请填写改进举措（做什么 + 谁负责 + 何时完成 + 衡量标准）"
    return null
}

/** 未签约项目人力投入复盘校验（spec 5.2）
 * worktimes 为员工数据填错的成员工时列表状态（校验勾选必填规则用，口径与已签约 A1 一致） */
export const validateUnsignedForm = (
    value: UnsignedReviewFormValue,
    worktimes: WorktimesState = { loaded: false, error: false, count: 0 }
): string | null => {
    switch (value.reasonType) {
        case "":
            return "请选择原因类型"
        case "wrong_initiation":
            return value.correctProjectId ? null : "立项错误：请选择正确项目"
        case "data_error": {
            const selected = value.dataErrorWorktimes ?? []
            // 工时列表为空或加载失败时不强制勾选（避免接口异常导致无法填报）
            if (worktimes.loaded && !worktimes.error && worktimes.count > 0 && selected.length === 0)
                return "员工数据填错：请勾选填错的人员（工时列表）"
            // 修正后的工日数仅允许大于等于 0 的数字
            if (
                selected.some(
                    (w) => w.correctedTotalHours !== undefined && (isNaN(w.correctedTotalHours) || w.correctedTotalHours < 0)
                )
            )
                return "修正后的工日数仅支持大于等于 0 的数字"
            return value.dataErrorDesc.trim() ? null : "员工数据填错：请填写具体描述"
        }
        case "will_sign":
            if (!value.expectedSignMonth) return "会签约：请填写预计签约时间"
            if (value.expectedSignAmount === undefined || value.expectedSignAmount === null)
                return "会签约：请填写预计签约金额"
            return null
        case "other":
            return value.otherDesc.trim() ? null : "其他原因：请填写具体描述"
        default:
            return null
    }
}
